import React, { useEffect, useRef } from "react";
import {
  List,
  ListItemButton,
  Paper,
  Typography,
  Chip,
  Divider,
  Card,
  Box,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { AuthorizationBatch } from "./authorization-types";
import {
  JobStatus,
  colorByStatus,
  statusToLabel,
} from "../../pages/playground/types";
import { Folder, Label, Warning } from "@mui/icons-material";

interface JobAuthorizationBatchListProps {
  batches: AuthorizationBatch[];
  onSelectBatch: (batchId: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
}

export const JobAuthorizationBatchList: React.FC<
  JobAuthorizationBatchListProps
> = ({ batches, onSelectBatch, onLoadMore, hasMore, loadingMore }) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, onLoadMore]);
  if (!batches || batches.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body1" color="textSecondary">
          No batches available
        </Typography>
      </Paper>
    );
  }

  return (
    <Card elevation={2} sx={{ height: "100%", overflow: "auto" }}>
      <List disablePadding dense sx={{ overflow: "auto" }}>
        {batches.map((batch) => {
          const hasUnassignedJobs = batch.unassigned_jobs_count > 0;

          return (
            <React.Fragment key={batch.id}>
              <ListItemButton
                onClick={() => onSelectBatch(batch.id)}
                sx={{
                  borderColor: "primary.main",
                  py: 1,
                  px: 2,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Batch name row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Folder
                          color="primary"
                          fontSize="small"
                          sx={{ mr: 0.5 }}
                        />
                        <Typography
                          variant="body1"
                          noWrap
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          {batch.name}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Label
                          fontSize="small"
                          color="secondary"
                          sx={{ mr: 0.5 }}
                        />
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          noWrap
                        >
                          {batch.brand.name}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Assignment progress indicator */}
                    <Tooltip
                      title={`${batch.assigned_jobs_count} SKU assegnati su ${batch.jobs_count} totali`}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {hasUnassignedJobs && (
                          <Warning color="error" fontSize="small" />
                        )}
                        <Chip
                          size="small"
                          label={`${batch.unassigned_jobs_count} to assign of ${batch.jobs_count}`}
                          color={hasUnassignedJobs ? "error" : "success"}
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            "& .MuiChip-label": { px: 1 },
                            fontWeight: hasUnassignedJobs ? 600 : 400,
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Status pills */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mr: 1 }}
                      >
                        Job statuses ({batch.jobs_count} total):
                      </Typography>
                      {/* Display statuses in consistent enum order */}
                      {(() => {
                        // Convert status_counts to an array and map keys to JobStatus enum
                        const statusEntries = Object.entries(
                          batch.status_counts
                        ).map(([statusKey, count]) => {
                          // Map the string key to the actual JobStatus enum
                          let status: JobStatus;
                          try {
                            status = statusKey as JobStatus;
                            if (!Object.values(JobStatus).includes(status)) {
                              status = JobStatus.TO_BE_STARTED;
                            }
                          } catch (e) {
                            status = JobStatus.TO_BE_STARTED;
                          }
                          return { status, count };
                        });

                        // Sort based on the order in JobStatus enum
                        // Create a mapping of status to position in the enum
                        const statusOrder: Record<string, number> = {};
                        Object.values(JobStatus).forEach((status, index) => {
                          statusOrder[status] = index;
                        });

                        // Sort the entries based on their enum position
                        statusEntries.sort(
                          (a, b) =>
                            (statusOrder[a.status] ?? Infinity) -
                            (statusOrder[b.status] ?? Infinity)
                        );

                        // Render the chips in sorted order
                        return statusEntries.map(({ status, count }) => {
                          const chipColor = colorByStatus[status];
                          const formattedStatus = statusToLabel(status);

                          return (
                            <Chip
                              key={status}
                              size="small"
                              label={`${formattedStatus}: ${count}`}
                              sx={{
                                borderRadius: 2,
                                height: 22,
                                fontSize: "0.7rem",
                                "& .MuiChip-label": { px: 1 },
                                borderColor: chipColor,
                                color: chipColor,
                                "&:hover": {
                                  backgroundColor: `${chipColor}22`, // 22 adds transparency
                                },
                              }}
                              variant="outlined"
                            />
                          );
                        });
                      })()}
                    </Box>

                    {/* Assigned copywriters */}
                    {batch.assigned_copywriters.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 0.5,
                          gap: 0.5,
                        }}
                      >
                        {batch.assigned_copywriters.map((copywriter) => (
                          <Chip
                            sx={{
                              borderRadius: 2,
                              height: 22,
                              fontSize: "0.7rem",
                            }}
                            label={
                              copywriter.name +
                              " " +
                              copywriter.surname +
                              " (" +
                              (copywriter.function === "Editor"
                                ? "Copywriter"
                                : "Reviewer") +
                              ")"
                            }
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </ListItemButton>
              <Divider />
            </React.Fragment>
          );
        })}

        {/* Infinite scroll trigger */}
        {hasMore && (
          <Box ref={loadMoreRef} sx={{ py: 2, textAlign: "center" }}>
            {loadingMore && <CircularProgress size={24} />}
          </Box>
        )}

        {/* End of list message */}
        {!hasMore && batches.length > 0 && (
          <Box sx={{ py: 2, textAlign: "center" }}>
            <Typography variant="caption" color="textSecondary">
              No more batches to load
            </Typography>
          </Box>
        )}
      </List>
    </Card>
  );
};
